import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, ClipboardList, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/recruiting/StatusBadge";
import { PriorityBadge, StageBadge } from "@/components/recruiting/PipelineBadges";
import { NewApplicationDialog } from "@/components/recruiting/NewApplicationDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { api } from "@/lib/api";
import { STATUSES, formatDate } from "@/lib/constants";

export default function RecruiterApplications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [apps, setApps] = useState([]);
  const [masterData, setMasterData] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [newAppOpen, setNewAppOpen] = useState(false);
  const status = searchParams.get("status") || "all";

  useEffect(() => {
    api.get("/master-data").then((res) => setMasterData(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/applications", { params: { status, q: q || undefined, stage } })
        .then((res) => setApps(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [status, q, stage]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">My applications</h1>
          <p className="text-muted-foreground mt-1 text-sm">Applications you own. Keep statuses updated after every contact.</p>
        </div>
        <Button onClick={() => setNewAppOpen(true)} className="rounded-lg shrink-0" data-testid="recruiter-new-application-button">
          <Plus className="h-4 w-4 mr-2" /> New Application
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="recruiter-apps-search" placeholder="Search name, mobile, application ID, job..." className="pl-9 bg-white" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="recruiter-apps-stage-filter">
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {(masterData.application_stage || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setSearchParams(v === "all" ? {} : { status: v })}>
          <SelectTrigger className="w-full sm:w-56 bg-white" data-testid="recruiter-apps-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : apps.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={ClipboardList} title="No applications found" description="Pick up applications from the Unassigned pool to start working." testId="recruiter-apps-empty" />
        </Card>
      ) : (
        <div className="space-y-3" data-testid="recruiter-applications-list">
          {apps.map((app) => (
            <Link key={app.id} to={`/recruiter/applications/${app.id}`} className="block" data-testid="recruiter-application-card">
              <Card className="rounded-xl border-border shadow-none hover:shadow-[var(--shadow-sm)] hover:border-primary/40 transition-all">
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <span className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
                      {(app.member?.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium leading-tight">{app.member?.name || app.candidate?.full_name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono">{app.member?.mobile || app.candidate?.mobile}</span> · <Building2 className="h-3 w-3" />{app.job?.hiring_brand} · {app.job?.title} · {formatDate(app.created_at)} · <span className="font-mono">{app.app_code || app.code}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {app.last_disposition && <span className="text-xs text-muted-foreground hidden sm:block">{app.last_disposition}</span>}
                      <PriorityBadge priority={app.priority} className="text-[11px]" />
                      <StageBadge stage={app.stage} className="text-[11px]" />
                      <StatusBadge status={app.status} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <NewApplicationDialog open={newAppOpen} onOpenChange={setNewAppOpen} isAdmin={false} />
    </div>
  );
}
